import { api } from "@/lib/http.ts";
import type { UploadFileReq, UploadFileResp } from "@/types/files.ts";

interface FileUploadResult {
  fileId: string;
}

export const uploadFile = async (file: File): Promise<FileUploadResult> => {
  console.log("Starting file upload:", {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
  });

  // Step 1: Get upload URL from our backend
  const res = (
    await api.post<UploadFileResp, UploadFileReq>("/api/a/files/upload", {
      filename: file.name,
      contentType: file.type,
    })
  ).data;

  // Step 2: Build headers including metadata. Use the server's canonical
  // contentType (it signed the URL with this), not the browser's `file.type` —
  // these differ for types like `.rtf` (browser: `text/rtf`, canonical:
  // `application/rtf`), and a mismatch fails the GCS v4 signature check.
  const headers: Record<string, string> = {
    "Content-Type": res.contentType ?? file.type,
    "x-goog-meta-filename": res.filename,
  };

  // Step 3: Upload file to the signed URL with all required headers
  console.log("Uploading file to GCS:", {
    url: res.signedUrl,
    headers,
    fileType: file.type,
    fileName: file.name,
  });

  const uploadRes = await fetch(res.signedUrl, {
    method: "PUT",
    body: file,
    headers,
  });

  if (!uploadRes.ok) {
    console.error("Upload failed:", {
      status: uploadRes.status,
      statusText: uploadRes.statusText,
      headers: Object.fromEntries(uploadRes.headers.entries()),
    });
    const errorText = await uploadRes.text();
    console.error("Error response:", errorText);
    throw new Error(`File upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
  }

  return {
    fileId: res.fileId,
  };
};
